/**
 * SSO/SCIM API Client
 */

import { api } from '@/lib/api-client';
import type {
  SSOConfig,
  SSOConfigInput,
  SSOConfigResponse,
  SCIMConfig,
  SCIMConfigInput,
  SCIMConfigResponse,
  SCIMTokenResponse,
  SCIMGroup,
  SCIMGroupsResponse,
  DomainVerificationResponse,
  SSOCheckApiResponse,
} from './types';

const SSO_BASE = '/api/v1/sso';
const SCIM_BASE = '/api/v1/scim';

// ============================================
// SSO Configuration
// ============================================

/**
 * Get SSO configuration for the organization
 */
export async function getSSOConfig(): Promise<SSOConfigResponse> {
  return api.get<SSOConfigResponse>(`${SSO_BASE}/config`);
}

/**
 * Create or update SSO configuration
 */
export async function saveSSOConfig(config: SSOConfigInput): Promise<SSOConfigResponse> {
  return api.post<SSOConfigResponse>(`${SSO_BASE}/config`, config);
}

/**
 * Delete SSO configuration
 */
export async function deleteSSOConfig(): Promise<{ success: boolean }> {
  return api.delete(`${SSO_BASE}/config`);
}

/**
 * Enable or disable SSO
 */
export async function toggleSSO(enabled: boolean): Promise<SSOConfigResponse> {
  return api.patch<SSOConfigResponse>(`${SSO_BASE}/config`, { is_enabled: enabled });
}

/**
 * Set SSO enforcement
 */
export async function enforceSSSO(enforced: boolean): Promise<SSOConfigResponse> {
  return api.patch<SSOConfigResponse>(`${SSO_BASE}/config`, { is_enforced: enforced });
}

/**
 * Get domain verification instructions
 */
export async function getDomainVerification(domain: string): Promise<DomainVerificationResponse> {
  return api.get<DomainVerificationResponse>(`${SSO_BASE}/config/verify-domain`, {
    params: { domain },
  });
}

/**
 * Verify a domain
 */
export async function verifyDomain(domain: string): Promise<DomainVerificationResponse> {
  return api.post<DomainVerificationResponse>(`${SSO_BASE}/config/verify-domain`, { domain });
}

/**
 * Check if SSO is required for an email
 */
export async function checkSSO(email: string): Promise<SSOCheckApiResponse> {
  return api.post<SSOCheckApiResponse>(`${SSO_BASE}/check`, { email });
}

/**
 * Initiate SSO test login
 */
export async function testSSOLogin(): Promise<{ success: boolean; data: { redirect_url: string } }> {
  return api.post(`${SSO_BASE}/test`);
}

/**
 * Get SP metadata for SAML
 */
export async function getSAMLMetadata(): Promise<string> {
  const response = await fetch(`${SSO_BASE}/saml/metadata`);
  return response.text();
}

// ============================================
// SCIM Configuration
// ============================================

/**
 * Get SCIM configuration
 */
export async function getSCIMConfig(): Promise<SCIMConfigResponse> {
  return api.get<SCIMConfigResponse>(`${SCIM_BASE}/config`);
}

/**
 * Update SCIM configuration
 */
export async function updateSCIMConfig(config: SCIMConfigInput): Promise<SCIMConfigResponse> {
  return api.patch<SCIMConfigResponse>(`${SCIM_BASE}/config`, config);
}

/**
 * Generate new SCIM token
 */
export async function generateSCIMToken(): Promise<SCIMTokenResponse> {
  return api.post<SCIMTokenResponse>(`${SCIM_BASE}/config/token`);
}

/**
 * Rotate SCIM token
 */
export async function rotateSCIMToken(): Promise<SCIMTokenResponse> {
  return api.post<SCIMTokenResponse>(`${SCIM_BASE}/config/token/rotate`);
}

/**
 * Trigger manual SCIM sync
 */
export async function triggerSCIMSync(): Promise<{ success: boolean; message: string }> {
  return api.post(`${SCIM_BASE}/sync`);
}

// ============================================
// SCIM Groups
// ============================================

/**
 * List SCIM groups
 */
export async function listSCIMGroups(): Promise<SCIMGroupsResponse> {
  return api.get<SCIMGroupsResponse>(`${SCIM_BASE}/groups`);
}

/**
 * Update group role mapping
 */
export async function updateGroupMapping(
  groupId: string,
  mapping: { role: string; permissions?: string[] }
): Promise<{ success: boolean; data: SCIMGroup }> {
  return api.patch(`${SCIM_BASE}/groups/${groupId}`, {
    mapped_role: mapping.role,
    mapped_permissions: mapping.permissions || [],
  });
}

// ============================================
// Export all functions
// ============================================

export default {
  // SSO
  getSSOConfig,
  saveSSOConfig,
  deleteSSOConfig,
  toggleSSO,
  enforceSSSO,
  getDomainVerification,
  verifyDomain,
  checkSSO,
  testSSOLogin,
  getSAMLMetadata,
  // SCIM
  getSCIMConfig,
  updateSCIMConfig,
  generateSCIMToken,
  rotateSCIMToken,
  triggerSCIMSync,
  listSCIMGroups,
  updateGroupMapping,
};
